import { cn } from '@brew/ui/cn'
import { VideoEmbedFrame } from '@/components/ui/video-embed-frame'

export const FOUNDER_WALKTHROUGH_LOOM_EMBED_URL =
  'https://www.loom.com/embed/dc325b42a4b54c50b1e5cdea3964e66a'

export const BREW_FOUNDERS_CALENDAR_URL =
  'https://calendar.google.com/calendar/appointments/schedules/AcZssZ1iYoRUG1J792XQpbuQLjSRRDupr7MwraFK-HQRCtTYdBmrQi8nZu2qXfzKQigb8gbKJK3KN3-R'

type FounderWalkthroughCardProps = {
  variant?: 'feature' | 'compact'
  className?: string
}

/**
 * Founder walkthrough card. The default `feature` variant mirrors the hero
 * `DemoFrame` layout: full-width Loom embed on top, hairline divider, then a
 * meta strip with title + body on the left and "Talk to Brew Founders" CTA
 * on the right. `compact` is reserved for in-app surfaces (e.g. brand
 * setup) and renders a tighter split layout.
 */
export function FounderWalkthroughCard({
  variant = 'feature',
  className,
}: FounderWalkthroughCardProps) {
  if (variant === 'compact') {
    return <CompactWalkthroughCard className={className} />
  }

  return (
    <section
      aria-label="Brew walkthrough"
      className={cn('overflow-hidden border border-border bg-card', className)}
    >
      <VideoEmbedFrame
        src={FOUNDER_WALKTHROUGH_LOOM_EMBED_URL}
        title="Welcome to Brew walkthrough"
        loading="lazy"
      />
      <div className="flex flex-col items-start justify-between gap-5 border-border border-t p-5 text-left md:flex-row md:items-center md:p-6">
        <div className="max-w-xl">
          <p className="font-medium text-primary text-xs">Brew walkthrough</p>
          <h3 className="mt-1 font-medium text-base text-foreground">
            See Brew in action.
          </h3>
          <p className="mt-1 text-muted-foreground text-sm leading-6">
            See how Brew learns a brand, creates emails from natural language,
            supports direct editing, and helps teams ship more lifecycle
            marketing.
          </p>
        </div>
        <div className="flex shrink-0 items-start">
          <a
            href={BREW_FOUNDERS_CALENDAR_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 font-medium text-primary text-sm transition-colors hover:opacity-80"
          >
            Talk to Brew Founders <span aria-hidden="true">→</span>
          </a>
        </div>
      </div>
    </section>
  )
}

function CompactWalkthroughCard({ className }: { className?: string }) {
  return (
    <section
      aria-label="Brew walkthrough"
      className={cn(
        'overflow-hidden rounded-lg border border-border bg-background/85 shadow-sm backdrop-blur-sm',
        className
      )}
    >
      <div className="min-h-0 bg-muted/40">
        <VideoEmbedFrame
          className="min-h-0 overflow-hidden"
          src={FOUNDER_WALKTHROUGH_LOOM_EMBED_URL}
          title="Welcome to Brew walkthrough"
          loading="lazy"
        />
      </div>
      <div className="flex min-w-0 flex-col gap-2 p-3">
        <div className="min-w-0">
          <p className="font-medium text-caption text-primary">
            Brew walkthrough
          </p>
          <h3 className="mt-1 font-semibold text-foreground text-sm">
            See how Brew builds from your brand.
          </h3>
          <p className="mt-2 text-caption text-muted-foreground leading-relaxed">
            While Brew builds your brand model, see how setup turns into
            on-brand email creation.
          </p>
        </div>
        <div className="flex min-w-0 flex-wrap items-center gap-3 text-caption">
          <a
            href={BREW_FOUNDERS_CALENDAR_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-primary transition-colors hover:opacity-80"
          >
            Talk to Brew Founders <span aria-hidden="true">→</span>
          </a>
        </div>
      </div>
    </section>
  )
}
